import * as React from 'react'
/* eslint-disable max-lines */
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { type CampaignOption } from '@/pages/PublicGiveCampaignContext'
import { CampaignGiveLayout } from '@/pages/PublicGiveCampaignLayout'
import { Confirmation } from '@/pages/PublicGiveConfirmation'
import { GiveShell } from '@/pages/PublicGiveShell'
import { GivingContactVerification } from '@/components/donations/giving-contact-verification'
import { PublicGiveTributeFields } from '@/components/donations/public-give-tribute-fields'
import {
  GiveDisclosures,
  PublicGiveFeeCover,
  SummaryRow,
  formatCents,
} from '@/components/donations/public-give-shared'
import { getJSON, ApiError } from '@/lib/api'
import { currencySymbol } from '@/lib/money'
import { useGiveCheckout } from '@/pages/useGiveCheckout'
import type { Association } from '@/pages/StaffSignInPage'

interface WordingResponse {
  give: {
    tax_wording: string
    disclosures_footer?: string
    donation_minimum_cents: number
    fee_cover_enabled?: boolean
    fee_cover_cents?: number
    gift_amount_cents?: number
    fee_cover_total_cents?: number
    campaigns: CampaignOption[]
    preset_amounts_cents?: number[]
  }
}

// eslint-disable-next-line complexity
export function PublicGivePage({ association }: { association: Association }) {
  const params = new URLSearchParams(window.location.search)
  const screen = giveScreen(params)
  const outcome = giveOutcome(params)
  const [campaignId, setCampaignId] = React.useState(params.get('campaign') ?? '')
  const wording = useQuery({
    queryKey: ['public-give-wording', params.get('amount') ?? '25.00', campaignId],
    queryFn: () =>
      getJSON<WordingResponse>(
        `/api/v1/give/options?amount=${encodeURIComponent(params.get('amount') ?? '25.00')}${campaignId ? `&campaign_id=${encodeURIComponent(campaignId)}` : ''}`,
      ),
    retry: false,
  })
  const [amount, setAmount] = React.useState(params.get('amount') ?? '25.00')
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [verificationToken, setVerificationToken] = React.useState<string | null>(null)
  const [tributeType, setTributeType] = React.useState<'honor' | 'memory' | null>(null)
  const [honoreeName, setHonoreeName] = React.useState('')
  const [displayAnonymous, setDisplayAnonymous] = React.useState(false)
  const [feeCover, setFeeCover] = useFeeCover(wording.data?.give.fee_cover_enabled)
  const checkout = useGiveCheckout({
    name,
    email,
    amount,
    campaignId,
    feeCover,
    tributeType,
    honoreeName,
    displayAnonymous,
    verificationToken,
  })
  const amountValue = Number.parseFloat(amount)
  const minimumCents = wording.data?.give.donation_minimum_cents ?? 100
  const amountValid = Number.isFinite(amountValue) && amountValue * 100 >= minimumCents
  const taxWording = wording.data?.give.tax_wording
  const disclosuresFooter = wording.data?.give.disclosures_footer
  const feeCoverDetails = getFeeCoverDetails(wording.data)

  if (screen === 'confirmation') return <Confirmation association={association} />

  return (
    <GiveShell
      association={association}
      screen={screen}
      outcome={outcome}
      error={wording.isError}
      campaignId={campaignId}
    >
      {screen === 'amount' ? (
        <AmountScreen
          currency={association.currency ?? 'USD'}
          amount={amount}
          valid={amountValid}
          minimumCents={minimumCents}
          campaigns={wording.data?.give.campaigns ?? []}
          presetAmountsCents={wording.data?.give.preset_amounts_cents ?? []}
          campaignId={campaignId}
          onCampaignChange={setCampaignId}
          wording={taxWording}
          footer={disclosuresFooter}
          onAmountChange={setAmount}
        />
      ) : (
        <DetailsScreen
          currency={association.currency ?? 'USD'}
          name={name}
          email={email}
          campaigns={wording.data?.give.campaigns ?? []}
          campaignId={campaignId}
          wording={taxWording}
          footer={disclosuresFooter}
          checkout={checkout}
          feeCover={feeCover}
          feeCoverCents={feeCoverDetails.cents}
          feeCoverTotalCents={feeCoverDetails.totalCents}
          giftAmountCents={feeCoverDetails.giftCents}
          feeCoverEnabled={feeCoverDetails.enabled}
          onFeeCoverChange={setFeeCover}
          onNameChange={setName}
          onEmailChange={setEmail}
          onVerified={setVerificationToken}
          tributeType={tributeType}
          honoreeName={honoreeName}
          displayAnonymous={displayAnonymous}
          onTributeTypeChange={setTributeType}
          onHonoreeNameChange={setHonoreeName}
          onDisplayAnonymousChange={setDisplayAnonymous}
        />
      )}
    </GiveShell>
  )
}

function useFeeCover(enabled: boolean | undefined) {
  const [feeCover, setFeeCover] = React.useState(false)
  React.useEffect(() => setFeeCover(Boolean(enabled)), [enabled])
  return [feeCover, setFeeCover] as const
}

function getFeeCoverDetails(data: WordingResponse | undefined) {
  return {
    cents: data?.give.fee_cover_cents ?? 0,
    enabled: data?.give.fee_cover_enabled ?? false,
    giftCents: data?.give.gift_amount_cents ?? 0,
    totalCents: data?.give.fee_cover_total_cents ?? 0,
  }
}

function giveScreen(params: URLSearchParams) {
  if (window.location.pathname === '/give/confirmed' || params.get('give') === 'confirmed')
    return 'confirmation'
  return params.get('screen') === 'details' ? 'details' : 'amount'
}

function giveOutcome(params: URLSearchParams) {
  return params.get('give') === 'failed' ||
    params.get('give') === 'abandoned' ||
    params.has('session_id')
    ? 'failed'
    : null
}

function AmountScreen({
  currency,
  amount,
  valid,
  minimumCents,
  campaigns,
  presetAmountsCents,
  campaignId,
  onCampaignChange,
  wording,
  footer,
  onAmountChange,
}: {
  currency: string
  amount: string
  valid: boolean
  minimumCents: number
  campaigns: CampaignOption[]
  presetAmountsCents: number[]
  campaignId: string
  onCampaignChange: (value: string) => void
  wording?: string
  footer?: string
  onAmountChange: (value: string) => void
}) {
  const continueToDetails = () =>
    window.location.assign(
      `/give?screen=details&amount=${encodeURIComponent(amount)}${campaignId ? `&campaign=${encodeURIComponent(campaignId)}` : ''}`,
    )
  const form = (
    <section className="space-y-5 rounded-md border border-border bg-card p-5">
      <div>
        <h1 className="text-subheading font-semibold">Your gift</h1>
        <p className="mt-1 text-body-sm text-muted-foreground">
          Choose an amount to support this Association.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="give-campaign">Support</Label>
        <Select
          value={campaignId || 'general'}
          onValueChange={(value) => onCampaignChange(value === 'general' ? '' : value)}
        >
          <SelectTrigger id="give-campaign" className="h-8 bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General support</SelectItem>
            {campaigns.map((campaign) => (
              <SelectItem key={campaign.id} value={String(campaign.id)}>
                {campaign.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="give-amount">Gift amount</Label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-heading-sm text-muted-foreground">
            {currencySymbol(currency)}
          </span>
          <Input
            id="give-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => onAmountChange(event.target.value)}
            className="h-12 pl-7 pr-12 text-heading-sm font-semibold tabular-nums"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-caption text-muted-foreground">
            {currency}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {presetAmountsCents.map((cents) => (
            <Button
              key={cents}
              type="button"
              variant="outline"
              className="h-8 px-3"
              onClick={() => onAmountChange((cents / 100).toFixed(2))}
            >
              {currencySymbol(currency)}
              {(cents / 100).toFixed(2)}
            </Button>
          ))}
        </div>
        {!valid && (
          <p className="text-caption text-destructive">
            Gift must be at least {currencySymbol(currency)}
            {(minimumCents / 100).toFixed(2)}.
          </p>
        )}
      </div>
      <Button type="button" disabled={!valid} className="w-full" onClick={continueToDetails}>
        Continue
      </Button>
    </section>
  )

  if (campaignId)
    return (
      <CampaignGiveLayout
        campaigns={campaigns}
        campaignId={campaignId}
        wording={wording}
        footer={footer}
      >
        {form}
      </CampaignGiveLayout>
    )

  return (
    <div className="space-y-5">
      {form}
      <GiveDisclosures wording={wording} footer={footer} />
    </div>
  )
}
// eslint-disable-next-line max-lines-per-function
function DetailsScreen({
  name,
  email,
  campaigns,
  campaignId,
  wording,
  footer,
  checkout,
  feeCover,
  feeCoverCents,
  feeCoverTotalCents,
  giftAmountCents,
  currency,
  feeCoverEnabled,
  onFeeCoverChange,
  onNameChange,
  onEmailChange,
  onVerified,
  tributeType,
  honoreeName,
  displayAnonymous,
  onTributeTypeChange,
  onHonoreeNameChange,
  onDisplayAnonymousChange,
}: {
  name: string
  email: string
  campaigns: CampaignOption[]
  campaignId: string
  wording?: string
  footer?: string
  checkout: ReturnType<typeof useGiveCheckout>
  feeCover: boolean
  feeCoverCents: number
  feeCoverTotalCents: number
  giftAmountCents: number
  currency: string
  feeCoverEnabled: boolean
  onFeeCoverChange: (value: boolean) => void
  onNameChange: (value: string) => void
  onEmailChange: (value: string) => void
  onVerified: (token: string) => void
  tributeType: 'honor' | 'memory' | null
  honoreeName: string
  displayAnonymous: boolean
  onTributeTypeChange: (value: 'honor' | 'memory' | null) => void
  onHonoreeNameChange: (value: string) => void
  onDisplayAnonymousChange: (value: boolean) => void
}) {
  const form = (
    <section className="space-y-5 rounded-md border border-border bg-card p-5">
      <div>
        <h1 className="text-subheading font-semibold">Your details</h1>
        <p className="mt-1 text-body-sm text-muted-foreground">
          Enter your details to continue to secure card payment.
        </p>
      </div>
      <div className="space-y-1 border-b border-border pb-4 text-body-sm">
        <SummaryRow label="Gift" amount={formatCents(giftAmountCents, currency)} />
        {feeCover && (
          <SummaryRow label="Fee cover" amount={`+${formatCents(feeCoverCents, currency)}`} />
        )}
        <SummaryRow
          label="Total"
          amount={formatCents(feeCover ? feeCoverTotalCents : giftAmountCents, currency)}
          emphasized
        />
      </div>
      <PublicGiveFeeCover
        enabled={feeCoverEnabled}
        checked={feeCover}
        cents={feeCoverCents}
        currency={currency}
        onChange={onFeeCoverChange}
      />
      <PublicGiveTributeFields
        tributeType={tributeType}
        honoreeName={honoreeName}
        displayAnonymous={displayAnonymous}
        onTributeTypeChange={onTributeTypeChange}
        onHonoreeNameChange={onHonoreeNameChange}
        onDisplayAnonymousChange={onDisplayAnonymousChange}
      />
      <div className="grid gap-4">
        <div className="space-y-2">
          <Label htmlFor="give-name">Name</Label>
          <Input
            id="give-name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="give-email">Email</Label>
          <Input
            id="give-email"
            type="email"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
          />
        </div>
      </div>
      <GivingContactVerification email={email} onVerified={onVerified} />
      {checkout.isError && (
        <Alert>
          {checkout.error instanceof ApiError ? checkout.error.message : 'Unable to start payment.'}
        </Alert>
      )}
      <div className="flex items-center justify-between gap-3">
        <Button asChild type="button" variant="outline">
          <a href="/give">Back</a>
        </Button>
        <Button
          type="button"
          disabled={!name.trim() || !email.trim() || checkout.isPending}
          onClick={() => checkout.mutate()}
        >
          {checkout.isPending ? 'Opening payment…' : 'Continue with card'}
        </Button>
      </div>
      {!campaignId && <GiveDisclosures wording={wording} footer={footer} />}
    </section>
  )

  return (
    <CampaignGiveLayout
      campaigns={campaigns}
      campaignId={campaignId}
      wording={wording}
      footer={footer}
    >
      {form}
    </CampaignGiveLayout>
  )
}
